/**
 * Hover popover card for map markers on the MAP and OPS dashboards.
 *
 * Renders a floating MUI card anchored to a screen-space { x, y } position
 * returned by Leaflet's mouse events. Shows site name, type, connection
 * status, the site's active and recent alarms named and coloured exactly as
 * the sidebar rows and the site dashboard name them, load names + schedule
 * summary, group label (tenant-term-aware), and last contact time.
 *
 * Detail data is lazy-fetched on hover from the per-site map endpoint
 * (`/dashboard/map/site/<id>/`). A small module-level cache (capped at 50
 * entries) makes re-hovering instant. While loading, a compact skeleton
 * section is shown; fetch errors silently omit the detail section.
 *
 * Contains:
 * - MapMarkerPopover — the overlay card (default export)
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { formatDistanceToNow } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BaseAlarmChip from '@sophocles/components/AlarmChip';
import StatusChip from '@sophocles/components/StatusChip';
import { fetchDashboardMapSiteDetail } from '@sophocles/features/dashboards/api';
import { CHIP_STYLES } from '@sophocles/features/dashboards/chipStyles';
import { SITE_STATUS } from '@sophocles/features/map/mapState';
import { getHighestSeverity, severityStatusFamily } from '@sophocles/utils/alarmSeverity';
import { tAlarm as translateAlarmKey } from '@sophocles/utils/alarmTranslation';
import { getCustomerTerminologies } from '@sophocles/utils/customerUtils';
import { collapseAlarmChips } from '@sophocles/views/map/mapAlarmChips';
import { siteStatusLabel } from '@sophocles/views/map/mapStatusLabels';
import useMapAlarmChips from '@sophocles/views/map/useMapAlarmChips';
import AlarmChip from '@sophocles/views/sites/components/alarms/AlarmChip';

// Module-level LRU-style cache: Map<`${siteId}:${hours}`, detail>
// Capped at 50 entries; oldest entry evicted when full.
const DETAIL_CACHE = new Map();
const DETAIL_CACHE_MAX = 50;

function getCacheKey(siteId, hours) {
  return `${siteId}:${hours ?? ''}`;
}

function setCached(key, value) {
  if (DETAIL_CACHE.size >= DETAIL_CACHE_MAX) {
    const firstKey = DETAIL_CACHE.keys().next().value;
    DETAIL_CACHE.delete(firstKey);
  }
  DETAIL_CACHE.set(key, value);
}

// Locale-aware relative "last contact" time. Uses date-fns so the suffix
// and units are translated for the active language (the hand-rolled
// "Xm ago" form bypassed i18n entirely).
function formatLastContact(isoString, locale) {
  if (!isoString) return '';
  return formatDistanceToNow(new Date(isoString), { addSuffix: true, locale });
}

// Translates a schedule on/off value (HH:MM | "sunset[+/-N]" | "sunrise[+/-N]")
// into a display string using t() for the sun keywords.
function formatScheduleTime(value, t) {
  if (!value) return '';
  const sunriseMatch = value.match(/^sunrise([+-]\d+)?$/);
  const sunsetMatch = value.match(/^sunset([+-]\d+)?$/);
  if (sunriseMatch) {
    const base = t('mapMarkerPopover.sunrise', 'Sunrise');
    return sunriseMatch[1] ? `${base}${sunriseMatch[1]}` : base;
  }
  if (sunsetMatch) {
    const base = t('mapMarkerPopover.sunset', 'Sunset');
    return sunsetMatch[1] ? `${base}${sunsetMatch[1]}` : base;
  }
  return value;
}

function formatSchedule(schedules, t) {
  if (!schedules || schedules.length === 0) return null;
  return schedules.map(s => `${formatScheduleTime(s.on, t)} – ${formatScheduleTime(s.off, t)}`).join(', ');
}

// Derives a display type label from the loads array once detail is loaded.
// Falls back to site.is_digital before detail is available.
function deriveTypeLabel(loads, site, t) {
  if (!loads || loads.length === 0) {
    return site.is_digital ? t('mapMarkerPopover.digital', 'Digital') : t('mapMarkerPopover.static', 'Static');
  }
  const hasDigital = loads.some(l => l.is_digital);
  const hasStatic = loads.some(l => !l.is_digital);
  if (hasDigital && hasStatic) return t('mapMarkerPopover.mixed', 'Mixed');
  if (hasDigital) return t('mapMarkerPopover.digital', 'Digital');
  return t('mapMarkerPopover.static', 'Static');
}

const MAX_ALARM_CHIPS = 4;

/**
 * @param {{
 *   site: object|null,
 *   position: {x: number, y: number}|null,
 *   isBillboard: boolean,
 *   hours: number|null
 * }} props
 *
 * `hours` — the time-window filter active on the parent dashboard.
 * Null / undefined means no time window (MAP dashboard); omits the
 * `hours` query param and suppresses the "active in period" section.
 */
export default function MapMarkerPopover({ site, position, isBillboard = false, hours = null }) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = i18n.language?.startsWith('es') ? es : enUS;
  const terms = useMemo(() => getCustomerTerminologies({ is_billboard: isBillboard }, t), [isBillboard, t]);

  // The card serves whichever dashboard mounted it, so its alarm vocabulary
  // follows that dashboard's customer rather than the signed-in one.
  const tAlarm = useCallback((key, options) => translateAlarmKey(t, key, isBillboard, options), [t, isBillboard]);
  const resolveAlarmChips = useMapAlarmChips(tAlarm);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!site?.id) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    const cacheKey = getCacheKey(site.id, hours);
    if (DETAIL_CACHE.has(cacheKey)) {
      setDetail(DETAIL_CACHE.get(cacheKey));
      setDetailLoading(false);
      return;
    }

    // Abort any in-flight request from a previous hover
    abortControllerRef.current?.abort();
    clearTimeout(debounceTimerRef.current);

    setDetail(null);
    setDetailLoading(true);

    debounceTimerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const params = {};
      if (hours != null) params.hours = hours;

      fetchDashboardMapSiteDetail(site.id, { params, signal: controller.signal })
        .then(data => {
          setCached(cacheKey, data);
          setDetail(data);
          setDetailLoading(false);
        })
        .catch(err => {
          // Ignore aborted requests (component unmounted or site changed)
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
          // On any other error, silently omit detail section
          setDetailLoading(false);
        });
    }, 200);

    return () => {
      clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, [site?.id, hours]);

  if (!site || !position) return null;

  const isOffline = site.is_offline;
  const hasAlarm = site.has_alarm;

  // Prefer the worst severity from the loaded detail (immune to a
  // stale bulk-payload cache); fall back to the marker's max_severity.
  const detailSeverity = detail?.active_alarms?.length
    ? getHighestSeverity(detail.active_alarms, a => a?.severity)
    : null;
  const alarmSeverity = detailSeverity || site.max_severity;

  const statusFamily = isOffline
    ? 'neutral'
    : hasAlarm
      ? alarmSeverity
        ? severityStatusFamily(alarmSeverity)
        : 'bad'
      : 'ok';
  const statusLabel = siteStatusLabel(
    t,
    isOffline ? SITE_STATUS.OFFLINE : hasAlarm ? SITE_STATUS.ALARMING : SITE_STATUS.ONLINE,
  );

  const lastContact = formatLastContact(site.last_communication, dateFnsLocale);

  // Active alarms — at most MAX_ALARM_CHIPS shown, remainder as "+N more"
  const activeAlarms = detail?.active_alarms ?? [];
  const { visible: visibleActive, hidden: hiddenActive } = collapseAlarmChips(
    resolveAlarmChips(activeAlarms),
    MAX_ALARM_CHIPS,
  );

  // Recent alarms (cleared within the time window) — only shown when hours param is active
  const recentAlarms = resolveAlarmChips(hours != null ? (detail?.recent_alarms ?? []) : []);

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        left: position.x + 12,
        top: position.y - 12,
        zIndex: 1200,
        pointerEvents: 'none',
        minWidth: 200,
        maxWidth: 300,
        borderRadius: 2,
        p: 1.5,
        transform: 'translateY(-50%)',
        userSelect: 'none',
      }}
    >
      {/* Site name */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.3 }}>
        {site.name}
      </Typography>

      {/* Status + Type chips */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
        <StatusChip label={statusLabel} color={statusFamily} />
        {isBillboard && <Chip label={deriveTypeLabel(detail?.loads, site, t)} size="small" sx={CHIP_STYLES.default} />}
      </Stack>

      {/* Active alarm chips — per-category, replacing the old count chip */}
      {detailLoading && (
        <Box sx={{ mb: 0.75 }}>
          <Skeleton variant="rounded" width={120} height={20} />
        </Box>
      )}

      {!detailLoading && activeAlarms.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
          {visibleActive.map(chip => (
            <AlarmChip
              key={chip.key}
              categoryKey={chip.categoryKey}
              label={chip.label}
              tAlarm={tAlarm}
              count={chip.count}
              severity={chip.severity}
            />
          ))}
          {hiddenActive.length > 0 && (
            <Tooltip title={hiddenActive.map(chip => chip.label).join(', ')}>
              <BaseAlarmChip
                label={t('mapMarkerPopover.moreAlarms', {
                  count: hiddenActive.length,
                  defaultValue: '+{{count}} more',
                })}
                color="neutral"
              />
            </Tooltip>
          )}
        </Stack>
      )}

      {/* Alarms active within the time window but since cleared */}
      {!detailLoading && recentAlarms.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.4, fontStyle: 'italic' }}>
            {t('mapMarkerPopover.activeInPeriod', 'Active in period')}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {recentAlarms.map(chip => (
              <AlarmChip
                key={chip.key}
                categoryKey={chip.categoryKey}
                label={chip.label}
                tAlarm={tAlarm}
                count={chip.count}
                severity={chip.severity}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Loads list — per load: name, digital/static indicator, schedule */}
      {!detailLoading && detail?.loads && detail.loads.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          {detail.loads.map((load, i) => {
            const scheduleText = formatSchedule(load.schedules, t);
            const loadTypeLabel = isBillboard
              ? load.is_digital
                ? t('mapMarkerPopover.digital', 'Digital')
                : t('mapMarkerPopover.static', 'Static')
              : null;
            const loadName =
              load.name ||
              t('mapMarkerPopover.relayNumber', { number: load.relay_number, defaultValue: 'Relay {{number}}' });
            return (
              <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {loadName}
                </Box>
                {loadTypeLabel && (
                  <>
                    {' · '}
                    <Box component="span" sx={{ opacity: 0.75 }}>
                      {loadTypeLabel}
                    </Box>
                  </>
                )}
                {scheduleText && (
                  <>
                    {' · '}
                    {scheduleText}
                  </>
                )}
              </Typography>
            );
          })}
        </Box>
      )}

      {/* Group name */}
      {site.site_group_name && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {terms.siteGroup}:
          </Box>{' '}
          {site.site_group_name}
        </Typography>
      )}

      {/* Last contact */}
      {lastContact && (
        <Typography variant="caption" color="text.secondary" display="block">
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t('mapMarkerPopover.lastContact', 'Last contact')}:
          </Box>{' '}
          {lastContact}
        </Typography>
      )}
    </Paper>
  );
}
